/**
 * UnifiedAiButton Component
 * 
 * Global floating action button to open the LifeFit360 AI Assistant.
 * Appears on Subject Management and Reports pages only.
 * Provides context-aware welcome messages based on current page.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Tooltip } from 'antd';
import ReportsChatbot from '../../modules/reports/components/reports-chatbot/reports-chatbot';
import useSmartApiClient from '../../hooks/useSmartApiClient';
import './unified-ai-button.css';

/**
 * Generate context-aware welcome message
 */
const getWelcomeMessage = (route, contextData) => {
  // Individual subject detail page with full context
  if (route.includes('/subject/') && contextData && contextData.subjectId) {
    const { displayId, firstName, lastName, tasksLoaded, tasksCount, tasksSummary,
            analyticsLoaded, analytics, surveyLoaded, onboardingData, enrollmentData } = contextData;
    
    return `Hello! I'm your LifeFit360 AI Assistant.

I can help you analyze **${displayId}**${firstName && lastName ? ` (${firstName} ${lastName})` : ''}:

**Available Data:**
${tasksLoaded ? `✓ **Tasks** - ${tasksCount} total (${tasksSummary && tasksSummary.completed} completed, ${tasksSummary && tasksSummary.pending} pending)` : ''}
${analyticsLoaded && analytics ? `✓ **Analytics** - Completion rate: ${analytics.completionRate}%, Adherence: ${analytics.adherenceRate}%` : ''}
${surveyLoaded && onboardingData ? `✓ **Onboarding** - ${onboardingData.completed}/${onboardingData.total} surveys completed` : ''}
${enrollmentData && enrollmentData.length > 0 ? `✓ **Enrollment** - ${enrollmentData.length} fields available` : ''}

Ask me anything about this subject!`;
  }
  
  // Subject management list pages
  if (route.includes('/subject-management')) {
    return `Hello! I'm your LifeFit360 AI Assistant.

I can help you analyze your subject population:

- **Subject Data** - Active subjects, cohorts, enrollment status
- **Task Analytics** - Adherence rates, completion statistics
- **Engagement Metrics** - Login patterns, activity scores
- **Scenario Progress** - Completion rates and responses

Ask me questions like:
- "How many active subjects are in Cohort A?"
- "Which subjects have low task adherence?"
- "Show me subjects who haven't logged in for 7 days"`;
  }
  
  // Reports and analytics pages
  if (route.includes('/reports')) {
    return `Hello! I'm your LifeFit360 AI Assistant.

I can help you analyze reports and analytics:

- **Scenario Analytics** - Question completion rates, response statistics
- **Task Analytics** - Daily task adherence, completion rates
- **Onboarding Analytics** - Survey completion, response data
- **Article Engagement** - Likes, dislikes, sentiment analysis
- **Audit Trails** - Subject activity, scenario interactions, user actions, chatbot logs

Ask me questions like:
- "What's the average task adherence rate this month?"
- "Which scenarios have the lowest completion?"
- "Show me recent subject activity trends"`;
  }
  
  // Fallback (shouldn't happen based on button placement)
  return `Hello! I'm your LifeFit360 AI Assistant.

I can help you with:
- **Subject Management** - Active subjects, task adherence, engagement
- **Reports & Analytics** - Scenario completion, task analytics, audit trails

Ask me anything!`;
};

const UnifiedAiButton = ({
  userId,
  currentRoute,
  projectId = null,
  contextData = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Get smart API client based on route
  const { apiClient, progressHook } = useSmartApiClient(currentRoute, null);

  // Generate context-aware welcome message
  const welcomeMessage = useMemo(
    () => getWelcomeMessage(currentRoute, contextData),
    [currentRoute, contextData]
  );

  // Build unified context object
  const unifiedContext = useMemo(() => {
    // If on subject detail page, use subjectContext format
    if (currentRoute.includes('/subject/') && contextData) {
      return {
        currentRoute,
        projectId: contextData.projectId || projectId,
        activeFilters: contextData.activeFilters || {},
        visibleDataSummary: contextData.visibleDataSummary || contextData,
        siteIds: contextData.siteIds || null
      };
    }
    
    // Otherwise use reportContext format
    return {
      currentRoute,
      projectId,
      activeFilters: (contextData && contextData.activeFilters) || {},
      visibleDataSummary: (contextData && contextData.visibleDataSummary) || null,
      siteIds: (contextData && contextData.siteIds) || null
    };
  }, [currentRoute, contextData, projectId]);

  const handleClick = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      {/* Floating Button */}
      <Tooltip
        title="LifeFit360 AI Assistant"
        placement="left"
      >
        <button
          className={`unified-ai-button ${isOpen ? 'active' : ''}`}
          onClick={handleClick}
          aria-label="Open LifeFit360 AI Assistant"
        >
          {isOpen ? (
            <span className="unified-ai-button-icon" style={{ fontSize: '28px' }}>×</span>
          ) : (
            <svg
              className="unified-ai-button-icon"
              width="26"
              height="26"
              viewBox="0 0 26 26"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8.17266 4.31878L6.34833 3.71067L5.74022 1.88634C5.54233 1.297 4.56733 1.297 4.36944 1.88634L3.76133 3.71067L1.93699 4.31878C1.64233 4.417 1.44299 4.69289 1.44299 5.00345C1.44299 5.314 1.64233 5.58989 1.93699 5.68811L3.76133 6.29623L4.36944 8.12056C4.46766 8.41523 4.74499 8.61456 5.05555 8.61456C5.3661 8.61456 5.64199 8.41523 5.74166 8.12056L6.34977 6.29623L8.1741 5.68811C8.46877 5.58989 8.6681 5.314 8.6681 5.00345C8.6681 4.69289 8.46733 4.417 8.17266 4.31878Z" fill="black"/>
              <path d="M13.7222 3.97217L16.4839 10.9589L23.4722 13.7222L16.4839 16.4854L13.7222 23.4722L10.9589 16.4854L3.97217 13.7222L10.9589 10.9589L13.7222 3.97217Z" stroke="black" strokeWidth="1.73333" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>
      </Tooltip>

      {/* Chatbot Modal */}
      {isOpen && (
        <ReportsChatbot
          userId={userId}
          currentRoute={unifiedContext.currentRoute}
          activeFilters={unifiedContext.activeFilters}
          visibleDataSummary={unifiedContext.visibleDataSummary}
          projectId={unifiedContext.projectId}
          siteIds={unifiedContext.siteIds}
          onClose={handleClose}
          apiClient={apiClient}
          progressTrackingHook={progressHook}
          title="LifeFit360 AI"
          welcomeMessage={welcomeMessage}
          localStoragePrefix="unifiedAiChatbot"
        />
      )}
    </>
  );
};

export default UnifiedAiButton;
